import { apiUrl } from "../config";
import { clientDeviceMeta } from "../clientMeta";
import { signGeneric, signGet, signPostScore } from "../crypto/bundleCrypto";
import type { EnrolledSession } from "../models/types";
import { getDeviceId } from "../storage/device";

export class ApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string; message?: string };
  if (!res.ok) {
    const code = data.error ?? null;
    const message = data.message || data.error || res.statusText;
    throw new ApiError(res.status, message, code);
  }
  return data;
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    body: JSON.stringify(body),
    credentials: "same-origin",
    ...init,
  });
  return parseJson<T>(res);
}

export async function apiGet<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: "GET",
    credentials: "same-origin",
    ...init,
  });
  return parseJson<T>(res);
}

export async function signedFetch(
  session: EnrolledSession,
  method: "GET" | "POST" | "DELETE",
  path: string,
  bodyCanonical: string,
  init?: RequestInit,
): Promise<Response> {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signGeneric(
    session.signing_secret_b64,
    method,
    path,
    bodyCanonical,
    timestamp,
  );
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${session.session_token}`);
  headers.set("X-Sundius-Timestamp", String(timestamp));
  headers.set("X-Sundius-Signature", signature);
  return fetch(apiUrl(path), {
    method,
    credentials: "same-origin",
    ...init,
    headers,
  });
}

export async function signedGet<T>(
  session: EnrolledSession,
  path: string,
  queryExtra = "",
): Promise<T> {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signGet(
    session.signing_secret_b64,
    path,
    queryExtra,
    timestamp,
  );
  return apiGet<T>(path, {
    headers: {
      Authorization: `Bearer ${session.session_token}`,
      "X-Sundius-Timestamp": String(timestamp),
      "X-Sundius-Signature": signature,
    },
  });
}

/** Fetch encrypted bundle/photo bytes — requires enrolled session + HMAC. */
export async function fetchEncryptedContent(
  session: EnrolledSession,
  filename: string,
): Promise<ArrayBuffer> {
  const path = `/family/content/${filename}`;
  const res = await signedFetch(session, "GET", path, "");
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = (await res.json()) as { error?: string };
      message = data.error ?? message;
    } catch {
      /* binary or empty */
    }
    throw new ApiError(res.status, message);
  }
  return res.arrayBuffer();
}

export async function signedPost<T>(
  session: EnrolledSession,
  path: string,
  body: unknown,
  bodyCanonical: string,
): Promise<T> {
  const res = await signedFetch(session, "POST", path, bodyCanonical, {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseJson<T>(res);
}

export async function signedPostScore<T>(
  session: EnrolledSession,
  body: {
    entry_id: string;
    person_id: string;
    device_id: string;
    display_name: string;
    mode: string;
    score: number;
    total: number;
    duration_ms: number;
    completed_at: number;
  },
): Promise<T> {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signPostScore(
    session.signing_secret_b64,
    body,
    timestamp,
  );
  return apiPost<T>("/scores", body, {
    headers: {
      Authorization: `Bearer ${session.session_token}`,
      "X-Sundius-Timestamp": String(timestamp),
      "X-Sundius-Signature": signature,
    },
  });
}

export function normalizeInviteCode(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!trimmed) return null;
  const normalized = trimmed.startsWith("sund_") ? trimmed : `sund_${trimmed}`;
  if (!/^sund_[a-z0-9-]+$/.test(normalized)) return null;
  return normalized;
}

export interface AuthSessionResponse {
  person_id: string;
  display_name: string;
  bundle_key_b64: string;
  signing_secret_b64: string;
  session_token: string;
  expires_at: number;
}

export async function inspectDevice(deviceId: string) {
  return apiPost<{
    enrolled: boolean;
    person_id: string;
    display_name: string;
  }>("/auth/inspect-device", { device_id: deviceId });
}

export async function resumeDevice(deviceId: string) {
  return apiPost<AuthSessionResponse>("/auth/resume", {
    device_id: deviceId,
    ...clientDeviceMeta(),
  });
}

export async function refreshSession(session: EnrolledSession) {
  const deviceId = getDeviceId();
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signGeneric(
    session.signing_secret_b64,
    "POST",
    "/auth/refresh",
    deviceId,
    timestamp,
  );
  return apiPost<AuthSessionResponse>(
    "/auth/refresh",
    {
      device_id: deviceId,
      session_token: session.session_token,
      ...clientDeviceMeta(),
    },
    {
      headers: {
        Authorization: `Bearer ${session.session_token}`,
        "X-Sundius-Timestamp": String(timestamp),
        "X-Sundius-Signature": signature,
      },
    },
  );
}

/** True when the server confirmed this device/member is gone — safe to wipe local session. */
export function isDefinitiveEnrollmentRevoke(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  // Resume/inspect rate limits intentionally return a generic "device not enrolled"
  // body with 429 — that must not wipe a valid local session.
  if (error.status === 429) return false;
  if (error.status !== 401 && error.status !== 403 && error.status !== 404) {
    return false;
  }
  const lower = `${error.message} ${error.code ?? ""}`.toLowerCase();
  if (lower.includes("device not enrolled") || lower.includes("member disabled")) {
    return true;
  }
  return error.status === 404;
}

export async function leaveDevice(session: EnrolledSession) {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signGeneric(
    session.signing_secret_b64,
    "POST",
    "/auth/leave-device",
    getDeviceId(),
    timestamp,
  );
  return apiPost<{ ok: boolean }>(
    "/auth/leave-device",
    { device_id: getDeviceId() },
    {
      headers: {
        Authorization: `Bearer ${session.session_token}`,
        "X-Sundius-Timestamp": String(timestamp),
        "X-Sundius-Signature": signature,
      },
    },
  );
}

export async function inspectInvite(code: string) {
  return apiPost<{
    person_id: string;
    display_name: string;
    invite_kind: string;
    relationship_note?: string | null;
  }>("/auth/inspect-invite", { invite_code: code });
}

export async function redeemInvite(code: string, deviceId: string, label: string) {
  return apiPost<AuthSessionResponse>("/auth/redeem", {
    invite_code: code,
    device_id: deviceId,
    device_label: label,
    ...clientDeviceMeta(),
  });
}

export async function requestAccess(payload: {
  device_id: string;
  requester_name: string;
  sharer_name: string;
  latitude: number;
  longitude: number;
  accuracy_m?: number | null;
  device_label?: string | null;
  suggested_person_id?: string | null;
  reason?: string | null;
}) {
  return apiPost<{ request_id: string; status: string }>(
    "/auth/request-access",
    payload,
  );
}

export async function requestAccessStatus(deviceId: string, requestId: string) {
  return apiPost<{ request_id: string; status: string }>(
    "/auth/request-access/status",
    { device_id: deviceId, request_id: requestId },
  );
}

export async function personalMatchName(
  firstName: string,
  lastName: string,
  deviceId: string,
) {
  return apiPost<{
    challenge_id: string;
    person_id: string;
    display_name: string;
    challenge_path: string;
    partner_prompt_copy?: string | null;
    already_enrolled: boolean;
    avatar_b64?: string | null;
  }>("/auth/personal/match-name", {
    first_name: firstName,
    last_name: lastName,
    device_id: deviceId,
  });
}

async function personalVerify(
  path: string,
  deviceId: string,
  payload: Record<string, string>,
) {
  return apiPost<{ ok: boolean; message?: string }>(path, {
    ...payload,
    device_id: deviceId,
  });
}

export const personalVerifyBirthday = (deviceId: string, p: Record<string, string>) =>
  personalVerify("/auth/personal/verify-birthday", deviceId, p);
export const personalVerifyPartner = (deviceId: string, p: Record<string, string>) =>
  personalVerify("/auth/personal/verify-partner", deviceId, p);
export const personalVerifyPhone = (deviceId: string, p: Record<string, string>) =>
  personalVerify("/auth/personal/verify-phone", deviceId, p);
export const personalVerifyState = (deviceId: string, p: Record<string, string>) =>
  personalVerify("/auth/personal/verify-state", deviceId, p);
export const personalVerifyWedding = (deviceId: string, p: Record<string, string>) =>
  personalVerify("/auth/personal/verify-wedding", deviceId, p);

export async function personalComplete(challengeId: string, deviceId: string, label: string) {
  return apiPost<AuthSessionResponse>("/auth/personal/complete", {
    challenge_id: challengeId,
    device_id: deviceId,
    device_label: label,
    ...clientDeviceMeta(),
  });
}

export async function fetchLeaderboard(session: EnrolledSession, mode: string, limit = 50) {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signGet(
    session.signing_secret_b64,
    "/leaderboard",
    mode,
    timestamp,
  );
  const query = `mode=${encodeURIComponent(mode)}&limit=${limit}`;
  return apiGet<{
    mode: string;
    updated_at: number;
    entries: Array<{
      rank: number;
      person_id: string;
      display_name: string;
      mode: string;
      score: number;
      total: number;
      percent: number;
      duration_ms: number;
      completed_at: number;
    }>;
  }>(`/leaderboard?${query}`, {
    headers: {
      Authorization: `Bearer ${session.session_token}`,
      "X-Sundius-Timestamp": String(timestamp),
      "X-Sundius-Signature": signature,
    },
  });
}

export async function fetchNewsFeed(session: EnrolledSession) {
  return signedGet<import("../models/types").NewsFeed>(session, "/family/news-feed", "");
}

export async function fetchUpcomingEvents(session: EnrolledSession) {
  return signedGet<{ events: import("../models/types").UpcomingEvent[] }>(
    session,
    "/family/upcoming-events",
    "",
  );
}

export async function fetchAnnouncements(session: EnrolledSession) {
  return signedGet<{ announcements: import("../models/types").Announcement[] }>(
    session,
    "/announcements",
    "",
  );
}

export async function fetchFeedbackReplies(session: EnrolledSession) {
  return signedGet<{ replies: import("../models/types").FeedbackReply[] }>(
    session,
    "/family/feedback/replies",
    "",
  );
}

export async function submitUsageEvents(
  session: EnrolledSession,
  events: Array<{ event_name: string; screen?: string | null; client_ts?: number | null }>,
) {
  const canonicalParts = events
    .map((e) => `${e.event_name}|${e.screen ?? ""}`)
    .sort()
    .join("|");
  const canonical = `usage|${canonicalParts}`;
  return signedPost(session, "/family/usage-events", { events }, canonical);
}
